import React, { useCallback, useState } from 'react';
import makeStyles from '@material-ui/core/styles/makeStyles';
import {
  createTicket,
} from '../../../actions/freshdesk-actions';
import ReactQuill from 'react-quill';
import { FormattedMessage, useIntl } from 'react-intl';
import CommonButton from '../../common/CommonButton';
import { Field, Form, Formik } from 'formik';
import Lottie from 'lottie-react';
import successAnimation from '../../../style/animations/success.json';
import PropTypes from 'prop-types';
import { bindActionCreators, compose } from 'redux';
import { connect } from 'react-redux';
import { Box } from '@mui/material';
import {
  ALMOST_WHITE_COLOR,
  BACKGROUND_DARK,
  BLACK_COLOR, EXPAND_ROW_COLOR_DARK, GREY_COLOR,
  VERY_LIGHT_GREY_COLOR,
  WHITE_COLOR,
} from '../../../const/color-const';
import { Button } from '@material-ui/core';
import { FRESHDESK_URL } from '../../../const/routes';
import FormTextField from '../../common/form/FormTextField';
import { darken } from '@material-ui/core/styles';
import { useHistory } from 'react-router';
import { useValidator } from '../../../utils/validators';
import classNames from 'classnames';
import CommonSnackbar from '../../common/CommonSnackbar';

export const editorStyles = theme => ({
  '& .quill': {
    borderRadius: '10px',
    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
    background: theme.palette.type === 'light' ? WHITE_COLOR : darken(BACKGROUND_DARK, 0.3),
    '& .ql-container': {
      borderBottomLeftRadius: '10px',
      borderBottomRightRadius: '10px',
      background: theme.palette.type === 'light' ? WHITE_COLOR : darken(BACKGROUND_DARK, 0.3),
    },
  },
  '& .ql-toolbar.ql-snow': {
    borderTopLeftRadius: '10px',
    borderTopRightRadius: '10px',
    padding: theme.spacing(1.5, 1, 1.5, 1),
  },
});

export const editorThemeStyles = theme => ({
  '& .ql-editor.ql-blank': {
    '&::before': {
      color: GREY_COLOR,
    },
  },
  '.quill > .ql-container > .ql-editor.ql-blank::before': {
    color: theme.palette.type === 'light' ? BLACK_COLOR : ALMOST_WHITE_COLOR,
  },
  '& .ql-snow .ql-fill, .ql-snow .ql-stroke.ql-fill': {
    fill: theme.palette.type === 'light' ? 'black' : VERY_LIGHT_GREY_COLOR,
  },
  '& .ql-snow .ql-stroke': {
    stroke: theme.palette.type === 'light' ? 'black' : VERY_LIGHT_GREY_COLOR,
  },
  '& .ql-toolbar .ql-fill': {
    fill: theme.palette.type === 'light' ? 'black' : VERY_LIGHT_GREY_COLOR,
    stroke: 'none',
  },
  '& .ql-toolbar, .ql-picker-label': {
    color: theme.palette.type === 'light' ? 'black' : VERY_LIGHT_GREY_COLOR,
  },
  '& .ql-container.ql-snow': {
    width: '100%',
    border: 'none',
  },
  '& .ql-toolbar, .ql-toolbar.ql-snow': {
    width: '100%',
    border: 'none',
    borderBottom: theme.palette.type === 'light' ? '1px solid lightgrey' : `1px solid ${BORDER_COLOR_DARK}`,
  },
  '& .view .ql-editor': {
    minHeight: 80,
  },
});

const BORDER_COLOR_DARK = '#3d3d46';

const useStyles = makeStyles(theme => ({
  '@keyframes loadEffect': {
    '0%': {
      transform: 'scale(0.95)',
      opacity: 0,
    },
    '100%': {
      transform: 'scale(1)',
      opacity: 1,
    },
  },
  ticketsWrapper: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    justifyContent: 'center',
    alignSelf: 'center',
    width: '100%',
    maxWidth: '800px',
    borderRadius: '8px',
    animation: `$loadEffect 500ms`,
    '& h2': {
      textAlign: 'center',
    },
  },
  freshdeskHeader: {
    alignSelf: 'center',
    paddingTop: theme.spacing(3),
  },
  description: {
    textAlign: 'center',
    margin: theme.spacing(2, 2, 3, 2),
  },
  formWrapper: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    padding: theme.spacing(1),
    '& .ql-editor': {
      minHeight: 120,
    },
  },
  
  quillWrapper: {
    ...editorStyles(theme),
    ...editorThemeStyles(theme),
    borderRadius: 10,
    border: theme.palette.type === 'light' ? '1px solid lightgrey' : `1px solid ${BORDER_COLOR_DARK}`,
  },
  fields: {
    width: '100%',
    display: 'flex',
    maxWidth: '720px',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
    '& [class*="MuiTextField"]': {
      margin: 0,
      width: '49%',
      background: theme.palette.type === 'light' ? WHITE_COLOR : darken(BACKGROUND_DARK, 0.3),
      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
      borderRadius: '10px',
      minWidth: 'unset',
      '& > div': {
        borderRadius: '10px',
      },
      '& p.Mui-error': {
        visibility: 'hidden',
        position: 'absolute',
        bottom: -16,
      },
    },
  },
  submitButton: {
    marginTop: theme.spacing(6),
  },
  btnWrapper: {
    margin: theme.spacing(2, 1, 2, 1),
  },
  navBtn: {
    width: 156,
    height: 40,
    margin: theme.spacing(2),
    color: theme.palette.type === 'light' ? BLACK_COLOR : ALMOST_WHITE_COLOR,
    backgroundColor: theme.palette.type === 'light' ? VERY_LIGHT_GREY_COLOR : EXPAND_ROW_COLOR_DARK,
    '&:hover': {
      background: theme.palette.type === 'light' ? darken(VERY_LIGHT_GREY_COLOR, 0.03) : darken(EXPAND_ROW_COLOR_DARK, 0.2),
    },
  },
  animationStyle: {
    width: 60,
    height: 60,
    marginBottom: 20,
    marginTop: 10,
  },
}));

export const EditorModules = {
  toolbar: [
    [{ 'header': '1' }, { 'header': '2' }, { 'font': [] }],
    [{ size: [] }],
    ['bold', 'italic', 'underline', 'strike', 'blockquote'],
    [{ 'list': 'ordered' }, { 'list': 'bullet' },
      { 'indent': '-1' }, { 'indent': '+1' }],
    ['link'], ['image'],
    ['clean'],
  ],
  clipboard: {
    matchVisual: false,
  },
};

export const EditorFormats = [
  'header', 'font', 'size',
  'bold', 'italic', 'underline', 'strike', 'blockquote',
  'list', 'bullet', 'indent',
  'link', 'image', 'video',
];

const validator = ({ values, intl: { formatMessage } }) => {
  const errors = {};
  
  if (!values.subject) {
    errors.subject = formatMessage({
      id: 'required',
      defaultMessage: 'Required',
    });
  }
  if (!values.email || !values.email.includes('@')) {
    const messageId = !values.email ? 'required' : 'wrong_email';
    errors.email = formatMessage({
      id: messageId,
      defaultMessage: messageId === 'required' ? 'Required' : 'Wrong email',
    });
  }
  return errors;
};

const generateNoteData = (Firstname, Lastname, EMail, machines) => {
  return `<p><strong>Name:</strong> ${Firstname} ${Lastname}</p><p> <strong>Email: </strong> ${EMail}</p><br>` +
    machines?.map(({ CustomerName, Name, Id }) =>
      `<p><strong>Customer Name</strong>: ${CustomerName},<br> <strong>Machine Name:</strong> ${Name},<br> <strong>ID:</strong> ${Id}</p>`,
    ).join('<br>');
};

const NewTicket = ({ createTicket, user, machines }) => {
  const [content, setContent] = useState(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isSnackbar, setIsSnackbar] = useState(false);
  const formValidator = useValidator(validator);
  const { EMail, Firstname, Lastname } = user;
  const { formatMessage } = useIntl();
  const history = useHistory();
  const classes = useStyles();
  
  const noteData = generateNoteData(Firstname, Lastname, EMail, machines);
  
  const redirectToTickets = () => {
    history.push(`${FRESHDESK_URL}/tickets`);
  };
  
  const handleNewTicket = useCallback(() => {
    setIsSuccess(true);
    setIsSnackbar(true);
  }, []);
  
  const successNotification = () => (
    <Box display={'flex'} flexDirection={'column'} justifyContent="center" alignItems="center">
      <Lottie loop={false} animationData={successAnimation} className={classes.animationStyle} play />
      <h2>
        <FormattedMessage
          id="freshdesk.ticket.sent"
          defaultMessage="Your ticket has been created. {break}We will get back to you as soon as we can!"
          values={{ break: <br /> }} />
      </h2>
      <Box className={classes.btnWrapper} display={'flex'} justifyContent={'center'} alignItems={'center'}>
        <Button className={classes.navBtn} onClick={() => setIsSuccess(false)}>
          <FormattedMessage id="freshdesk.new.ticket.content" defaultMessage="Create new ticket" />
        </Button>
        <Button className={classes.navBtn} onClick={() => redirectToTickets()}>
          <FormattedMessage id="my.tickets" defaultMessage="My tickets" />
        </Button>
      </Box>
  
      {isSnackbar && <CommonSnackbar
        open={isSnackbar}
        title={<FormattedMessage id="new.ticket.message" defaultMessage="New ticket created" />}
        message={<FormattedMessage id="new.ticket.created" defaultMessage="We will contact you as soon as possible!" />}
        timeframe={6000}
        onExtraClose={() => setIsSnackbar(false)}
      />}
    </Box>
  );
  
  return (
    <div className={classNames(classes.ticketsWrapper)}>
      <h1 className={classes.freshdeskHeader}>
        <FormattedMessage id="new.ticket" defaultMessage="New ticket" />
      </h1>
      <p className={classes.description}>
        <FormattedMessage id="freshdesk.new.ticket.description1"
                          defaultMessage="You can use this form to quickly <b>create</b> a customer support ticket."
                          values={{ b: (chunks) => <strong>{chunks}</strong> }}
        /> <br />
        <FormattedMessage id="freshdesk.new.ticket.description2"
                          defaultMessage="Fill in the details and we'll assist you as soon as possible. You can follow the status of your ticket under <b>'My  tickets</b>'."
                          values={{ b: (chunks) => <strong>{chunks}</strong> }}
        />
      </p>
    
      {!isSuccess && <Formik
        enableReinitialize
        validate={formValidator}
        initialValues={{ email: EMail }}
        onSubmit={(values) => {
          createTicket(values.subject, content, EMail, noteData).then(() => handleNewTicket());
        }}>
        {({ isSubmitting, isValid, dirty }) => (
          <Form className={classNames(classes.formWrapper)}>
            <div className={classes.fields}>
              <Field
                name="subject"
                component={FormTextField}
                label={(
                  <FormattedMessage
                    id="subject"
                    defaultMessage="Subject"
                  />
                )}
              />
      
              <Field
                disabled
                name="email"
                component={FormTextField}
                label={(
                  <FormattedMessage
                    id="freshdesk.email"
                    defaultMessage="E-mail"
                  />
                )}
              />
            </div>
    
            <div className={classes.quillWrapper}>
              <ReactQuill
                placeholder={formatMessage({
                  id: 'freshdesk.placeholder',
                  defaultMessage: 'Describe your case thoroughly',
                })}
                theme="snow"
                value={content}
                onChange={setContent}
                formats={EditorFormats}
                modules={EditorModules}
              />
            </div>
            <CommonButton
              className={classes.submitButton}
              disabled={isSubmitting || !isValid || !dirty || !content}
              isAnimated
              type="submit"
              size="large"
              label={<FormattedMessage id="freshdesk.create.ticket" defaultMessage="Create ticket" />}
            />
          </Form>
        )}
      </Formik>}
    
      {isSuccess && successNotification()}
    </div>
  );
};

NewTicket.propTypes = {
  createTicket: PropTypes.func,
  user: PropTypes.object,
  machines: PropTypes.array,
};

const decorate = compose(
  connect(store => ({
      user: store.user.profile,
      machines: store.user.machines,
    }),
    dispatch => bindActionCreators({ createTicket }, dispatch),
  ),
);

export default decorate(NewTicket);
